import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { accountSubject, type AuthenticatedAccount } from './account';

interface SubjectLifetime {
  active: boolean;
  subject: string;
}

const AuthenticatedSubjectContext = createContext<SubjectLifetime | null>(null);

/**
 * Scopes a signed-in tree to one account. Its children remount when the account
 * changes, so forms, one-time secrets and every other piece of component state
 * from the previous account are dropped rather than shown to the next one.
 */
export const AuthenticatedSubjectProvider = ({
  account,
  children,
}: {
  account: AuthenticatedAccount;
  children: ReactNode;
}) => {
  const subject = accountSubject(account);
  const lifetime = useMemo<SubjectLifetime>(() => ({ active: true, subject }), [subject]);
  useLayoutEffect(() => {
    // React Strict Mode deliberately runs setup -> cleanup -> setup in development.
    // Reactivating here keeps the mounted lifetime usable after that probe while
    // the cleanup still permanently retires it when the subject tree is replaced.
    lifetime.active = true;
    return () => { lifetime.active = false; };
  }, [lifetime]);
  return (
    <AuthenticatedSubjectContext.Provider key={subject} value={lifetime}>
      {children}
    </AuthenticatedSubjectContext.Provider>
  );
};

const useSubjectLifetime = (): SubjectLifetime => {
  const lifetime = useContext(AuthenticatedSubjectContext);
  if (!lifetime) throw new Error('AuthenticatedSubjectProvider is required');
  return lifetime;
};

/** Account subject (person plus account) for a tree that has passed an authentication guard. */
export const useAuthenticatedSubject = (): string => {
  return useSubjectLifetime().subject;
};

/** Tests whether the exact mounted subject lifetime is still current. */
export const useAuthenticatedSubjectIsCurrent = (): (() => boolean) => {
  const lifetime = useSubjectLifetime();
  return useCallback(() => lifetime.active, [lifetime]);
};

/** Runs a side effect only while the exact mounted subject lifetime is current. */
export const useAuthenticatedSubjectEffect = (): ((effect: () => void) => void) => {
  const lifetime = useSubjectLifetime();
  return useCallback((effect: () => void) => {
    if (lifetime.active) effect();
  }, [lifetime]);
};
